import { useCallback, useEffect, useState, type RefObject } from 'react';

export interface UseFullscreenResult {
  isFullscreen: boolean;
  toggle: () => Promise<void>;
}

// iOS Safari (iPhone) doesn't implement the standard Fullscreen API on
// arbitrary elements — `Element.requestFullscreen` is undefined — but it does
// expose a fullscreen API directly on <video> elements via these webkit-
// prefixed members. We feature-detect them rather than UA-sniff.
interface WebkitVideo extends HTMLVideoElement {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
}

/**
 * Drives fullscreen for the player. Preference order:
 *
 *   1. `Element.requestFullscreen()` on the WRAPPER (desktop + Android Chrome).
 *      Requesting on the wrapper — not the bare <video> — keeps our overlay
 *      children (settings popover, unmute pill, etc.) visible in fullscreen.
 *   2. `video.webkitEnterFullscreen()` (iOS Safari). iPhone only implements
 *      fullscreen on the <video> element itself; this is REAL fullscreen (iOS
 *      paints its own native controls over it). We lose our custom overlay in
 *      that mode, which is an acceptable trade for actually going fullscreen.
 *   3. CSS-only "pseudo-fullscreen" (position: fixed; inset: 0) as a last
 *      resort for the rare browser exposing neither API.
 *
 * Earlier this hook went (1) → (3) and skipped the iOS <video> path, so on
 * iPhone tapping fullscreen fell through to pseudo-fullscreen — which just
 * enlarges the player inside the page instead of truly going fullscreen.
 */
const PSEUDO_FS_CLASS = 'is-pseudo-fullscreen';

export function useFullscreen(
  elRef: RefObject<HTMLElement>,
  videoRef?: RefObject<HTMLVideoElement>,
): UseFullscreenResult {
  const [isFullscreen, setIsFullscreen] = useState<boolean>(
    typeof document !== 'undefined' && document.fullscreenElement != null,
  );
  // Tracks the pseudo-fullscreen state separately so toggle() can flip it
  // without going through the (missing) browser event loop.
  const [pseudoFs, setPseudoFs] = useState<boolean>(false);
  // Tracks iOS native <video> fullscreen, which doesn't fire `fullscreenchange`
  // and doesn't set `document.fullscreenElement`.
  const [videoFs, setVideoFs] = useState<boolean>(false);

  useEffect(() => {
    function onChange(): void {
      setIsFullscreen(document.fullscreenElement === elRef.current);
    }
    document.addEventListener('fullscreenchange', onChange);
    return (): void => document.removeEventListener('fullscreenchange', onChange);
  }, [elRef]);

  // iOS fires webkitbegin/endfullscreen on the <video> when it enters/leaves
  // its native fullscreen (including when the user taps iOS's own "Done").
  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return undefined;
    function onBegin(): void { setVideoFs(true); }
    function onEnd(): void { setVideoFs(false); }
    video.addEventListener('webkitbeginfullscreen', onBegin as EventListener);
    video.addEventListener('webkitendfullscreen', onEnd as EventListener);
    return (): void => {
      video.removeEventListener('webkitbeginfullscreen', onBegin as EventListener);
      video.removeEventListener('webkitendfullscreen', onEnd as EventListener);
    };
  }, [videoRef]);

  // Lock body scroll while in pseudo-fullscreen so iOS Safari's rubber-band
  // can't reveal the URL bar over our chrome. Cleanup on unmount or exit.
  useEffect(() => {
    if (pseudoFs) document.documentElement.classList.add(PSEUDO_FS_CLASS);
    else document.documentElement.classList.remove(PSEUDO_FS_CLASS);
    return (): void => document.documentElement.classList.remove(PSEUDO_FS_CLASS);
  }, [pseudoFs]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    // Escape leaves pseudo-fullscreen for keyboard users.
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && pseudoFs) {
        setPseudoFs(false);
      }
    }
    document.addEventListener('keydown', onKey);
    return (): void => document.removeEventListener('keydown', onKey);
  }, [pseudoFs]);

  const toggle = useCallback(async (): Promise<void> => {
    const el = elRef.current;
    if (!el) return;
    const video = videoRef?.current as WebkitVideo | null | undefined;

    // ── EXIT paths ──
    // Already in pseudo-fullscreen? Just flip the flag — no API to call.
    if (pseudoFs) {
      setPseudoFs(false);
      return;
    }
    // Native element fullscreen (desktop / Android).
    if (document.fullscreenElement === el && typeof document.exitFullscreen === 'function') {
      try { await document.exitFullscreen(); return; } catch { /* fall through */ }
    }
    // iOS native <video> fullscreen.
    if (video && video.webkitDisplayingFullscreen && typeof video.webkitExitFullscreen === 'function') {
      try { video.webkitExitFullscreen(); return; } catch { /* fall through */ }
    }

    // ── ENTER paths ──
    // Wrapped in try/catch because Safari can throw synchronously when the call
    // isn't allowed (no user gesture, sandbox, iframe missing `allowfullscreen`,
    // etc.); we fall through to the next strategy on rejection.
    if (typeof el.requestFullscreen === 'function') {
      try {
        await el.requestFullscreen();
        return;
      } catch { /* fall through to <video> fullscreen */ }
    }
    // iOS Safari: only the <video> element can go fullscreen.
    if (video && typeof video.webkitEnterFullscreen === 'function') {
      try {
        video.webkitEnterFullscreen();
        return;
      } catch { /* fall through to pseudo */ }
    }
    setPseudoFs(true);
  }, [elRef, videoRef, pseudoFs]);

  return { isFullscreen: isFullscreen || pseudoFs || videoFs, toggle };
}
